import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { useRequestDocuments, type DocumentAnchor } from '../../hooks/useDocuments'
import {
  MERCHANT_UPLOADABLE_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
  type DocumentCategory,
} from '../../lib/api'
import { apiErrorMessage } from '../../hooks/useErrorHandler'

interface Props {
  anchor: DocumentAnchor
  merchantEmail: string | null
  merchantName: string | null
  onClose: () => void
}

export function RequestDocumentsModal({ anchor, merchantEmail, merchantName, onClose }: Props) {
  const requestDocs = useRequestDocuments()
  const dialogRef = useRef<HTMLDivElement>(null)

  const [email, setEmail] = useState(merchantEmail ?? '')
  const [name, setName] = useState(merchantName ?? '')
  const [message, setMessage] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<Set<DocumentCategory>>(new Set())
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const toggleCategory = (cat: DocumentCategory) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  const handleSubmit = () => {
    setError(null)

    if (selectedCategories.size === 0) {
      setError('Select at least one document category.')
      return
    }
    if (!email || !email.includes('@')) {
      setError('Enter a valid email address.')
      return
    }

    requestDocs.mutate(
      {
        anchor,
        data: {
          categories: Array.from(selectedCategories),
          merchant_email: email,
          merchant_name: name || undefined,
          message: message || undefined,
        },
      },
      {
        onSuccess: (resp) => {
          setSuccess(`Request sent to ${email}. Link expires ${new Date(resp.expires_at).toLocaleDateString()}.`)
        },
        onError: (err) => {
          setError(apiErrorMessage(err, 'Failed to send request.'))
        },
      },
    )
  }

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const firstControl = dialogRef.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])')
    firstControl?.focus()

    return () => {
      previousFocus?.focus()
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card
        ref={dialogRef}
        className="w-full max-w-lg mx-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-documents-title"
      >
        <CardHeader>
          <CardTitle id="request-documents-title">Request Documents from Merchant</CardTitle>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800" aria-live="polite">
                {success}
              </div>
              <div className="flex justify-end">
                <Button variant="secondary" onClick={onClose}>Close</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="document-request-email" className="block text-sm font-medium text-gray-700 mb-1">Merchant Email</label>
                <input
                  id="document-request-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="merchant@example.com"
                />
              </div>

              {/* Name */}
              <div>
                <label htmlFor="document-request-name" className="block text-sm font-medium text-gray-700 mb-1">Merchant Name</label>
                <input
                  id="document-request-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Business name or contact name"
                />
              </div>

              {/* Category checkboxes */}
              <div>
                <div id="document-request-categories-label" className="block text-sm font-medium text-gray-700 mb-2">Documents to Request</div>
                <div className="space-y-2" role="group" aria-labelledby="document-request-categories-label">
                  {MERCHANT_UPLOADABLE_CATEGORIES.map((cat) => (
                    <label key={cat} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCategories.has(cat)}
                        onChange={() => toggleCategory(cat)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{DOCUMENT_CATEGORY_LABELS[cat]}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="document-request-message" className="block text-sm font-medium text-gray-700 mb-1">Custom Message (optional)</label>
                <textarea
                  id="document-request-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Any additional instructions for the merchant..."
                />
              </div>

              {error && (
                <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800" role="alert">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button
                  onClick={handleSubmit}
                  isLoading={requestDocs.isPending}
                  disabled={selectedCategories.size === 0}
                >
                  Send Request
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
